// Receber várias comandas de uma vez (a mesma cliente com dois horários, ou duas clientes pagando juntas).
// Cada atendimento continua com a própria cobrança e comissão; o servidor reparte o pagamento e o desconto geral.
import { useRef, useState } from 'react';
import { api, newKey } from '../api.ts';
import { msg } from '../app/actions.ts';
import { useNav } from '../app/nav.tsx';
import { useToast } from '../app/toast.tsx';
import { can, useAgenda, useMachines, useMe, useRefresh, type AgendaItem } from '../lib/data.ts';
import { first, money, moneyShort, nowLocal, parseMoney } from '../lib/format.ts';
import { Chips } from '../ui/bits.tsx';
import { Icon } from '../ui/Icon.tsx';
import { Sheet } from '../ui/Sheet.tsx';
import { MachinePick, PixQr } from './ComandaSheet.tsx';

type Method = 'pix' | 'credit' | 'debit' | 'cash' | 'later';
const SHORT: Record<Method, string> = { pix: 'Pix', credit: 'Crédito', debit: 'Débito', cash: 'Dinheiro', later: 'Depois' };
const LONG: Record<Method, string> = { pix: 'no Pix', credit: 'no crédito', debit: 'no débito', cash: 'em dinheiro', later: 'depois' };
const DISC_WHY = ['Cliente fiel', 'Combinado antes', 'Compensação por um problema'];
const OPEN = ['pending', 'confirmed', 'arrived', 'in_service'];

const sum = (a: number[]) => a.reduce((t, v) => t + v, 0);
// divide um valor em partes proporcionais sem perder centavo (o mesmo cálculo do servidor)
function split(total: number, weights: number[]): number[] {
  const sumW = sum(weights);
  if (!sumW) return weights.map(() => 0);
  const raw = weights.map((w) => (total * w) / sumW);
  const out = raw.map(Math.floor);
  let left = total - sum(out);
  raw.map((v, i) => [v - Math.floor(v), i] as const).sort((x, y) => y[0] - x[0]).forEach(([, i]) => { if (left > 0) { out[i]!++; left--; } });
  return out;
}
const cents = (txt: string) => Math.max(0, Math.round((parseMoney(txt) || 0) * 100));

export function JuntasSheet({ ids: start }: { ids: string[] }) {
  const nav = useNav();
  const toast = useToast();
  const refresh = useRefresh();
  const me = useMe().data!;
  const settings = me.salon.settings;
  const machines = (useMachines().data ?? []).filter((m) => m.active);
  const today = nowLocal().date;
  const agenda = useAgenda(today).data ?? [];
  const key = useRef(newKey()).current;
  const [ids, setIds] = useState<string[]>(start);
  const [disc, setDisc] = useState<Record<string, string>>({}); // desconto de cada comanda, em reais digitados
  const [gen, setGen] = useState('');
  const [reason, setReason] = useState('');
  const [payer, setPayer] = useState<string>('each');
  const [method, setMethod] = useState<Method | null>(null);
  const [two, setTwo] = useState(false);
  const [mA, setMA] = useState<Method>('pix');
  const [mB, setMB] = useState<Method>('cash');
  const [vA, setVA] = useState('');
  const [machineId, setMachineId] = useState<string | null>(null);
  const [inst, setInst] = useState(1);
  const [given, setGiven] = useState('');
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState<{ total: number; names: string; troco: number } | null>(null);

  const rows = ids.map((id) => agenda.find((x) => x.id === id)).filter((x): x is AgendaItem => !!x);
  const others = agenda.filter((x) => OPEN.includes(x.status) && !ids.includes(x.id));
  const clients = [...new Map(rows.map((r) => [r.clientId, r.client])).entries()];

  // contas, na mesma ordem do servidor: o desconto geral é rateado pelo valor de cada comanda
  const subs = rows.map((r) => r.priceCents);
  const gCents = Math.min(sum(subs), cents(gen));
  const gShare = gCents > 0 ? split(gCents, subs) : subs.map(() => 0);
  const own = rows.map((r) => Math.min(r.priceCents, cents(disc[r.id] ?? '')));
  const discounts = rows.map((_, i) => own[i]! + gShare[i]!);
  const deposits = rows.map((r) => (r.depositStatus === 'received' ? r.depositCents : 0));
  const dues = rows.map((r, i) => Math.max(0, r.priceCents - discounts[i]! - deposits[i]!));
  const total = sum(dues);
  const totalDisc = sum(discounts);
  const pct = sum(subs) ? totalDisc / sum(subs) : 0;
  const needWhy = pct > 0.1 && !reason;

  const machine = machines.find((m) => m.id === machineId) ?? machines[0] ?? null;
  const instOptions = machine && settings.installments.on ? [1, ...Object.keys(machine.installmentBps).map(Number).filter((n) => n > 1 && n <= settings.installments.max).sort((x, y) => x - y)] : [1];
  const card = (m: Method) => (m === 'credit' || m === 'debit') && machine ? { machineId: machine.id, installments: m === 'credit' ? inst : 1 } : {};
  const methods: Method[] = [...(['pix', 'credit', 'debit', 'cash'] as Method[]).filter((m) => settings.methods[m as 'pix']), ...(settings.methods.later && can(me, 'pagarDepois') ? ['later' as Method] : [])];

  type Pay = { method: Method; amountCents: number; machineId?: string; installments?: number };
  const check = (): { ok: boolean; msg: string; cls?: string; payments: Pay[]; troco?: number } => {
    if (rows.length < 2) return { ok: false, msg: 'Junte pelo menos duas comandas.', payments: [] };
    if (total <= 0) return { ok: false, msg: 'Nada a receber nessas comandas.', cls: 'warn', payments: [] };
    if (!two) {
      if (!method) return { ok: false, msg: 'Escolha como foi pago.', payments: [] };
      if ((method === 'credit' || method === 'debit') && !machine) return { ok: false, msg: 'Cadastre a maquininha em Mais › Formas de pagamento.', cls: 'warn', payments: [] };
      if (method === 'cash' && given !== '') {
        const g = cents(given);
        if (g < total) return { ok: false, msg: `O valor recebido é menor que ${money(total)}.`, cls: 'bad', payments: [] };
        return { ok: true, msg: g === total ? 'Valor exato, sem troco.' : `Troco: ${money(g - total)}`, cls: 'ok', payments: [{ method, amountCents: total }], troco: g - total };
      }
      return { ok: true, msg: method === 'pix' ? 'O Pix fica "a conferir" até você bater com o extrato.' : method === 'later' ? 'Fica como pendência na ficha.' : '', payments: [{ method, amountCents: total, ...card(method) }] };
    }
    const va = cents(vA);
    if (va <= 0) return { ok: false, msg: `Digite quanto foi ${LONG[mA]}.`, cls: 'warn', payments: [] };
    if (va >= total) return { ok: false, msg: `A primeira forma precisa ser menor que ${money(total)}.`, cls: 'bad', payments: [] };
    if (mA === mB) return { ok: false, msg: 'Escolha duas formas diferentes.', cls: 'warn', payments: [] };
    return { ok: true, msg: `${money(va)} ${LONG[mA]} e ${money(total - va)} ${LONG[mB]}.`, cls: 'ok', payments: [{ method: mA, amountCents: va, ...card(mA) }, { method: mB, amountCents: total - va, ...card(mB) }] };
  };
  const chk = check();

  async function pay() {
    if (needWhy) { toast('Desconto acima de 10%: escolha o motivo.', 'warn'); return; }
    setBusy(true);
    try {
      const r = await api.post<{ totalCents: number }>('/checkout/batch', {
        idempotencyKey: key,
        items: rows.map((r2, i) => ({
          appointmentId: r2.id,
          discountCents: own[i]!,
          discountReason: own[i]! > 0 ? reason || `Desconto de ${Math.round((own[i]! / r2.priceCents) * 100)}%` : '',
        })),
        generalDiscountCents: gCents,
        generalDiscountReason: gCents > 0 ? reason || `Desconto geral de ${Math.round((gCents / sum(subs)) * 100)}%` : '',
        payerClientId: payer !== 'each' ? payer : undefined,
        payments: chk.payments,
      });
      setDone({ total: r.totalCents, names: rows.map((x) => first(x.client)).join(' + '), troco: chk.troco ?? 0 });
      await refresh();
    } catch (e) { toast(msg(e), 'warn'); await refresh(); } finally { setBusy(false); }
  }

  if (done) {
    return (
      <Sheet wide title="Recebido" sub={done.names} actions={<button className="cta" onClick={() => nav.close()}>Fechar</button>}>
        <div className="due-band"><b>{money(done.total)}</b><span><small>{done.names}</small></span></div>
        {done.troco > 0 && <p className="adj-note">Troco: {money(done.troco)}</p>}
        <p className="m-hint">Cada atendimento ficou concluído com a própria cobrança e comissão. Para estornar, abra a comanda de cada um.</p>
      </Sheet>
    );
  }

  const label = !two && !method ? `Escolha a forma · ${money(total)}` : `Receber ${money(total)}${two ? ' dividido' : method ? ` ${LONG[method as Method]}` : ''}`;
  return (
    <Sheet wide title="Receber juntas" sub={rows.map((r) => first(r.client)).join(' + ')}
      actions={<button className="cta" disabled={!chk.ok || busy} onClick={pay}>{busy ? 'Registrando…' : label}</button>}>
      <div className="due-band" aria-label={`A receber ${money(total)}`}><b>{money(total)}</b><span><small>{rows.length} comandas{totalDisc ? ` · desconto − ${money(totalDisc)}` : ''}</small></span></div>
      <p className="gl-title first">Comandas</p>
      <div className="gl">
        {rows.map((r, i) => (
          <div key={r.id} className="cline">
            <span className="name"><b>{r.client}</b><small>{r.service} · {r.time}{deposits[i] ? ` · sinal − ${money(deposits[i]!)}` : ''}</small></span>
            {can(me, 'mudarPreco') && (
              <div className="money-in" style={{ maxWidth: 120 }}><span>− R$</span><input inputMode="decimal" autoComplete="off" placeholder="desc." aria-label={`Desconto na comanda de ${first(r.client)}`} value={disc[r.id] ?? ''} onChange={(e) => setDisc({ ...disc, [r.id]: e.target.value })} /></div>
            )}
            <b>{money(dues[i]!)}</b>
            {rows.length > 2 && <button className="dec-btn" aria-label={`Tirar ${first(r.client)}`} onClick={() => setIds(ids.filter((x) => x !== r.id))}><Icon name="minus" /></button>}
          </div>
        ))}
        {others.length > 0 && (
          <div className="adj-panel"><p className="sv-hint">Juntar também</p>
            <div className="chipset">{others.slice(0, 12).map((x) => <button key={x.id} onClick={() => setIds([...ids, x.id])}>{first(x.client)} · {x.time} · {moneyShort(x.priceCents)}</button>)}</div></div>
        )}
      </div>

      {can(me, 'mudarPreco') && (
        <>
          <p className="gl-title">Desconto geral</p>
          <div className="money-in"><span>R$</span><input inputMode="decimal" autoComplete="off" placeholder="0,00" aria-label="Desconto geral" value={gen} onChange={(e) => setGen(e.target.value)} /></div>
          {pct > 0.1 && (
            <div className="sv-why need disc-why"><span>Desconto acima de 10%: qual o motivo?</span>
              <div className="chipset">{DISC_WHY.map((rz) => <button key={rz} className={reason === rz ? 'on' : ''} onClick={() => setReason(rz)}>{rz}</button>)}</div></div>
          )}
        </>
      )}

      {clients.length > 1 && (
        <>
          <p className="gl-title">Recibo e pendência no nome de</p>
          <Chips value={payer} options={[['each', 'Cada uma no seu'], ...clients.map(([id, name]) => [id, `Só ${first(name)}`] as [string, string])]} onChange={setPayer} />
          <p className="m-hint">{payer === 'each' ? 'Cada comanda fica no nome da própria cliente.' : 'Se ficar para pagar depois, a pendência vai para essa cliente.'}</p>
        </>
      )}

      <div className="section-title">Forma de pagamento</div>
      {!two ? (
        <>
          <div className="methods grid3">
            {methods.map((m) => <button key={m} className={method === m ? 'sel' : ''} aria-pressed={method === m} onClick={() => { setMethod(m); setInst(1); }}>{SHORT[m]}</button>)}
            <button onClick={() => setTwo(true)}>Duas formas</button>
          </div>
          {method === 'pix' && settings.pixKey.trim() && <PixQr amountCents={total} pixKey={settings.pixKey} salon={me.salon.name} address={settings.profile.address} />}
          {(method === 'credit' || method === 'debit') && machines.length > 1 && <div className="field pay-maq"><label>Maquininha</label><MachinePick machines={machines} value={machine?.id ?? null} onChange={setMachineId} /></div>}
          {method === 'credit' && instOptions.length > 1 && (
            <div className="field pay-parc"><label>Parcelas</label><div className="chipset soft">{instOptions.map((k) => <button key={k} className={inst === k ? 'on' : ''} aria-pressed={inst === k} onClick={() => setInst(k)}>{k === 1 ? 'À vista' : `${k}x de ${money(Math.ceil(total / k))}`}</button>)}</div></div>
          )}
          {method === 'cash' && <div className="field"><label htmlFor="jCash">Quanto ela entregou (deixe em branco se for exato)</label><div className="money-in"><span>R$</span><input id="jCash" inputMode="decimal" autoComplete="off" placeholder={(total / 100).toFixed(2).replace('.', ',')} value={given} onChange={(e) => setGiven(e.target.value)} /></div></div>}
        </>
      ) : (
        <>
          <div className="field"><label>Primeira forma</label>
            <div className="chipset soft">{methods.filter((m) => m !== 'later').map((m) => <button key={m} className={mA === m ? 'on' : ''} onClick={() => setMA(m)}>{SHORT[m]}</button>)}</div>
            <div className="money-in" style={{ marginTop: 8 }}><span>R$</span><input inputMode="decimal" autoComplete="off" placeholder="0,00" value={vA} onChange={(e) => setVA(e.target.value)} aria-label={`Valor ${LONG[mA]}`} /></div></div>
          <div className="field"><label>Segunda forma · recebe o restante</label>
            <div className="chipset soft">{methods.map((m) => <button key={m} className={mB === m ? 'on' : ''} onClick={() => setMB(m)}>{SHORT[m]}</button>)}</div></div>
          <button className="link-btn" onClick={() => setTwo(false)}>Usar uma forma só</button>
        </>
      )}
      <div className={'live ' + (chk.cls ?? '')} aria-live="polite">{chk.msg}</div>
    </Sheet>
  );
}
